import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Loader2, History, ChevronDown, ChevronRight, RotateCcw, GitCompareArrows } from 'lucide-react';
import { DiffView } from './DiffView';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CURRENT = '__current__';

const REASONS = {
  'client-name scrub': { label: 'Client-name scrub', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  'promotion review edit': { label: 'Promotion review edit', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  'import-update': { label: 'Replaced by import', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  'pre-restore': { label: 'Before a restore', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  'cleanup-improve': { label: 'Clean & consolidate', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  'cleanup-undo': { label: 'Cleanup undone', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  merge: { label: 'Merged', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  edit: { label: 'Manual edit', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};

/** Why a revision was archived — chip label + colour; promotions carry the source RFP name. */
const reasonLabel = (rev) => {
  const r = rev?.reason || 'edit';
  if (r.startsWith('promoted from RFP')) return { label: 'Promoted from RFP', cls: 'bg-cyan-50 text-cyan-700 border-cyan-200', title: r };
  const m = REASONS[r];
  return m ? { ...m, title: rev?.hits?.length ? `${r} — ${rev.hits.join(', ')}` : r } : { label: r, cls: 'bg-slate-50 text-slate-500 border-slate-200', title: r };
};

export const BlockHistoryDialog = ({ block, onOpenChange, canManage, onRestored }) => {
  const [revisions, setRevisions] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState([]); // up to 2 version ids for compare
  const [reasonFilter, setReasonFilter] = useState('all'); // chip label, or 'all'

  useEffect(() => {
    if (!block) return;
    setRevisions(null);
    setOpenIdx(null);
    setSelected([]);
    setReasonFilter('all');
    axios.get(`${API_URL}/api/rfp/boilerplate/${block.id}/history`, { withCredentials: true })
      .then(res => setRevisions(res.data?.revisions || []))
      .catch(() => { toast.error('Could not load history'); setRevisions([]); });
  }, [block]);

  const restore = async (rev) => {
    if (!window.confirm(`Restore the wording from ${new Date(rev.archived_at).toLocaleString()}? The current wording is archived first.`)) return;
    setBusyId(rev.id);
    try {
      await axios.post(`${API_URL}/api/rfp/boilerplate/${block.id}/restore/${rev.id}`, {}, { withCredentials: true });
      toast.success('Previous wording restored');
      onOpenChange(false);
      onRestored();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally {
      setBusyId(null);
    }
  };

  const toggleSelect = (id) => setSelected(sel =>
    sel.includes(id) ? sel.filter(x => x !== id) : [...sel.slice(-1), id]);

  const versions = [
    { id: CURRENT, title: block?.title, body: block?.body,
      label: 'Current version', when: block?.updated_at, by: block?.updated_by },
    ...(revisions || []).map(r => ({
      id: r.id, title: r.title, body: r.body, rev: r,
      label: r.title, when: r.archived_at, by: r.saved_by,
    })),
  ];
  // Reason filter: chips built from the reasons actually present on this block (counts included).
  const reasonCounts = (revisions || []).reduce((acc, r) => { const l = reasonLabel(r).label; acc[l] = (acc[l] || 0) + 1; return acc; }, {});
  const reasonChips = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1]);
  const visible = versions.filter(v => v.id === CURRENT || reasonFilter === 'all' || reasonLabel(v.rev).label === reasonFilter);
  // Older versions sit later in the list → diff older (old) vs newer (new).
  const picked = selected.map(id => versions.find(v => v.id === id)).filter(Boolean);
  const ordered = picked.length === 2
    ? [...picked].sort((a, b) => versions.findIndex(v => v.id === b.id) - versions.findIndex(v => v.id === a.id))
    : picked;

  const label = (v) => v.id === CURRENT
    ? 'Current version'
    : `${v.when ? new Date(v.when).toLocaleString() : 'Earlier version'}${v.by && v.by !== 'system' ? ` · ${v.by}` : ''}`;

  return (
    <Dialog open={!!block} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rfp-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4 text-[#4ABFCF]" />
            History — {block?.capability_id} {block?.title}
          </DialogTitle>
          <DialogDescription>
            Previous wordings, newest first. Tick any two versions to see exactly what changed. Restoring archives the current wording too.
          </DialogDescription>
        </DialogHeader>
        {revisions === null ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF]" /></div>
        ) : revisions.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center" data-testid="rfp-history-empty">
            No previous versions yet — history starts with the next edit.
          </p>
        ) : (
          <div className="space-y-2" data-testid="rfp-history-list">
            {reasonChips.length > 1 && (
              <div className="flex flex-wrap items-center gap-1" data-testid="rfp-history-filter">
                <span className="text-[10px] text-slate-400 mr-1">Show:</span>
                <button type="button" onClick={() => setReasonFilter('all')}
                  className={`text-[9px] px-2 py-px rounded-full border ${reasonFilter === 'all' ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  data-testid="rfp-history-filter-all">All ({revisions.length})</button>
                {reasonChips.map(([lbl, n]) => {
                  const meta = Object.values(REASONS).find(m => m.label === lbl) || (lbl === 'Promoted from RFP' ? { cls: 'bg-cyan-50 text-cyan-700 border-cyan-200' } : { cls: 'bg-slate-50 text-slate-500 border-slate-200' });
                  const active = reasonFilter === lbl;
                  return (
                    <button key={lbl} type="button" onClick={() => { setReasonFilter(active ? 'all' : lbl); setOpenIdx(null); }}
                      className={`text-[9px] px-2 py-px rounded-full border transition-colors ${meta.cls} ${active ? 'ring-2 ring-[#0F2040]/60 font-semibold' : 'opacity-80 hover:opacity-100'}`}
                      title={active ? 'Click to clear the filter' : `Show only "${lbl}" revisions`}
                      data-testid={`rfp-history-filter-${lbl.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{lbl} ({n})</button>
                  );
                })}
              </div>
            )}
            {reasonFilter !== 'all' && visible.length === 1 && (
              <p className="text-[11px] text-slate-400 text-center py-2" data-testid="rfp-history-filter-empty">No revisions match this filter.</p>
            )}
            {ordered.length === 2 && (
              <div className="border border-[#4ABFCF]/40 bg-[#4ABFCF]/5 rounded-md p-2.5" data-testid="rfp-history-compare">
                <p className="text-[10px] font-semibold text-[#0F2040] flex items-center gap-1 mb-1.5">
                  <GitCompareArrows className="w-3 h-3 text-[#4ABFCF]" />Comparing versions
                </p>
                <DiffView oldText={ordered[0].body} newText={ordered[1].body}
                  oldLabel={label(ordered[0])} newLabel={label(ordered[1])} />
              </div>
            )}
            {visible.map((v) => (
              <div key={v.id} className={`border rounded-md ${selected.includes(v.id) ? 'border-[#4ABFCF]' : 'border-slate-200'}`}
                data-testid={v.id === CURRENT ? 'rfp-history-current' : `rfp-history-rev-${v.id}`}>
                <div className="flex items-center gap-2 px-2.5 py-2">
                  <input type="checkbox" className="accent-[#4ABFCF]" checked={selected.includes(v.id)}
                    onChange={() => toggleSelect(v.id)} title="Tick two versions to compare"
                    data-testid={`rfp-history-pick-${v.id === CURRENT ? 'current' : v.id}`} />
                  {v.id !== CURRENT ? (
                    <button onClick={() => setOpenIdx(openIdx === v.id ? null : v.id)} className="flex items-center gap-1.5 flex-1 min-w-0 text-left">
                      {openIdx === v.id ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
                      <span className="text-xs font-medium text-[#0F2040] truncate">{v.title}</span>
                    </button>
                  ) : (
                    <span className="text-xs font-semibold text-[#0F2040] flex-1 min-w-0 truncate">Current version</span>
                  )}
                  {v.id !== CURRENT && reasonLabel(v.rev) && (
                    <span className={`text-[9px] px-1.5 py-px rounded-full border shrink-0 ${reasonLabel(v.rev).cls}`}
                      title={`Why this revision was archived: ${reasonLabel(v.rev).title}`} data-testid={`rfp-history-reason-${v.id}`}>
                      {reasonLabel(v.rev).label}
                    </span>
                  )}
                  <span className="text-[9px] text-slate-400 shrink-0">
                    {v.when ? new Date(v.when).toLocaleString() : ''}
                    {v.by && v.by !== 'system' ? ` · ${v.by}` : ''}
                  </span>
                  {canManage && v.id !== CURRENT && (
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 shrink-0"
                      onClick={() => restore(v.rev)} disabled={busyId === v.id}
                      data-testid={`rfp-history-restore-${v.id}`}>
                      {busyId === v.id ? <Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" /> : <RotateCcw className="w-2.5 h-2.5 mr-1" />}
                      Restore
                    </Button>
                  )}
                </div>
                {openIdx === v.id && v.id !== CURRENT && (
                  <p className="text-[11px] text-slate-600 whitespace-pre-line px-3 pb-2.5 max-h-48 overflow-y-auto border-t border-slate-100 pt-2">
                    {v.body}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
